import { useEffect, useMemo, useRef } from 'react';
import { getIsMacOs } from 'shared/utils/get-is-mac-os';

export type Options = {
  otherKeysInCombinationAllowed?: boolean;
};

export const useKeysDown = (
  keys: string[] | string[][],
  callback: (e: KeyboardEvent, keys: string[]) => void,
  { otherKeysInCombinationAllowed }: Options = {
    otherKeysInCombinationAllowed: false,
  },
): void => {
  const commandPressed = useRef(false);
  const pressed = useRef<string[]>([]);
  const keysToProcess: string[][] = useMemo(
    () =>
      Array.isArray(keys[0]) ? (keys as string[][]) : ([keys] as string[][]),
    [keys],
  );

  useEffect(() => {
    const isMacOs = getIsMacOs();
    const commandKeys = isMacOs
      ? ['MetaLeft', 'MetaRight']
      : ['ControlLeft', 'ControlRight'];

    const onKeyDown = (e: KeyboardEvent) => {
      const keyId = e.code || e.key;

      if (commandKeys.includes(keyId)) {
        commandPressed.current = true;
      }

      // figure out if the pressed key is one we track
      const isTrackedKey = keysToProcess.some((keys) => keys.includes(keyId));

      // if the key is not tracked, or it is already tracked part of the pressed keys
      if (
        commandPressed.current ||
        (otherKeysInCombinationAllowed && !isTrackedKey) ||
        pressed.current.includes(keyId)
      ) {
        return;
      }

      pressed.current.push(keyId);

      const keysPressed = keysToProcess.map((combinedKeys) =>
        combinedKeys.map((key) => pressed.current.includes(key)),
      );

      keysPressed.forEach((pressedKeys, index) => {
        if (
          pressedKeys.every(Boolean) &&
          pressedKeys.length === pressed.current.length
        ) {
          callback(e, keysToProcess[index]);
        }
      });
    };

    const onKeyUp = (e: KeyboardEvent) => {
      const keyId = e.code || e.key;
      if (commandKeys.includes(keyId)) {
        commandPressed.current = false;
      }

      pressed.current = pressed.current.filter((code) => code !== keyId);
    };
    const unregisterKeys = () => {
      pressed.current = [];
      commandPressed.current = false;
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', unregisterKeys);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('blur', unregisterKeys);
    };
  }, [callback, keys, keysToProcess, otherKeysInCombinationAllowed]);
};
